/**
 * Mandatory two-step verification, shown straight after sign-in to anyone who
 * has not set it up (the API refuses everything else until they have). Scan
 * the QR code, type the code it shows, keep the recovery codes — then in.
 */

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import QRCode from 'qrcode';

import { ApiError, beginTwoStep, confirmTwoStep, signOut } from '../api.ts';
import { LoginShell } from './LoginShell.tsx';

export function TwoStepSetup({ onDone }: { onDone: () => void }): ReactNode {
  const [secret, setSecret] = useState('');
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Starting setup issues a NEW secret and replaces the last one, so it must
  // run once: a second call (React's development double-run, a remount) would
  // leave the QR code showing a secret the server no longer holds.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    beginTwoStep()
      .then(async (enrolment) => {
        setSecret(enrolment.secret);
        setQr(await QRCode.toDataURL(enrolment.uri, { margin: 1, width: 200 }));
      })
      .catch((cause: unknown) =>
        setError(
          cause instanceof ApiError
            ? cause.message
            : 'Could not start the setup. Reload and try again.',
        ),
      );
  }, []);

  const verify = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setRecovery(await confirmTwoStep(code));
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : 'That code did not work. Try the newest one.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (recovery) {
    return (
      <LoginShell>
        <header className="li-head">
          <h1>Keep your recovery codes</h1>
          <p>Each code signs you in once if you lose your phone. They are shown only now.</p>
        </header>
        <ol className="li-recovery mono">
          {recovery.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ol>
        <label className="li-confirm">
          <input
            type="checkbox"
            checked={saved}
            onChange={(event) => setSaved(event.target.checked)}
          />
          I have saved these codes somewhere safe
        </label>
        <button type="button" className="li-submit" disabled={!saved} onClick={onDone}>
          Continue to SynAnalytics →
        </button>
      </LoginShell>
    );
  }

  return (
    <LoginShell>
      <header className="li-head">
        <h1>Turn on two-step verification</h1>
        <p>SynAnalytics requires it on every account. It takes a minute, once.</p>
      </header>
      {error && (
        <div className="li-error" role="alert">
          {error}
        </div>
      )}
      <ol className="li-steps">
        <li>
          Open an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password…).
        </li>
        <li>Scan this code, or type the key below.</li>
      </ol>
      <div className="li-qr">
        {qr ? (
          <img src={qr} width={200} height={200} alt="QR code for your authenticator app" />
        ) : (
          <span className="li-qr-wait">Preparing…</span>
        )}
      </div>
      {secret && (
        <p className="li-key">
          Key: <code className="mono">{secret.replace(/(.{4})/g, '$1 ').trim()}</code>
        </p>
      )}
      <form onSubmit={verify} className="li-form">
        <label htmlFor="setup-code">3. Enter the 6-digit code it shows</label>
        <input
          id="setup-code"
          className="li-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
        />
        <button className="li-submit" type="submit" disabled={busy || code.length < 6 || !secret}>
          {busy ? 'Checking…' : 'Turn on two-step verification →'}
        </button>
        <p className="li-after">
          <button
            type="button"
            className="li-link"
            onClick={() => {
              signOut();
              window.location.reload();
            }}
          >
            Sign out
          </button>
        </p>
      </form>
    </LoginShell>
  );
}
